// Mercapoli - Eliminación de Cuenta
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { auth as authApi } from '../../lib/api';
import { toast } from 'sonner';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";
  
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirmations, setConfirmations] = useState({
    understand: false,
    dataLoss: false,
    noRefund: false
  });
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const allConfirmed = confirmations.understand && confirmations.dataLoss && confirmations.noRefund;

  const handleDeleteAccount = async () => {
    if (!allConfirmed || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      await authApi.deleteAccount(password);
      setDeleted(true);
      setTimeout(() => {
        logout();
        navigate('/');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  if (deleted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Cuenta Eliminada</h1>
          <p className="text-muted-foreground mb-4">
            Tu cuenta y datos han sido eliminados exitosamente.
          </p>
          <p className="text-sm text-muted-foreground">
            Serás redirigido al inicio en unos segundos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Mercapoli" className="h-8 w-8" />
                <span className="font-bold text-lg">Mercapoli</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-lg">
        {/* Warning Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Eliminar Cuenta</h1>
          <p className="text-muted-foreground">
            Esta acción es permanente y no se puede deshacer.
          </p>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            {/* Warning Box */}
            <div className="bg-red-50 dark:bg-red-950/30 rounded-xl p-6 border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-800 dark:text-red-200 mb-2">
                    ¿Qué sucede al eliminar tu cuenta?
                  </h3>
                  <ul className="space-y-2 text-sm text-red-700 dark:text-red-300">
                    <li>• Se eliminarán permanentemente todos tus datos personales</li>
                    <li>• Perderás acceso a tu historial de pedidos</li>
                    <li>• Tus puntos DIGGII serán cancelados sin reembolso</li>
                    <li>• No podrás recuperar esta información</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <p className="text-sm text-muted-foreground mb-1">Cuenta a eliminar:</p>
              <p className="font-medium">{user?.email}</p>
              <p className="text-sm text-muted-foreground">{user?.nombre}</p>
            </div>

            {/* Confirmations */}
            <div className="space-y-4">
              <Label className="font-medium">Confirma que entiendes:</Label>
              
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="understand" 
                  checked={confirmations.understand}
                  onCheckedChange={(checked) => setConfirmations(prev => ({ ...prev, understand: checked }))}
                />
                <label htmlFor="understand" className="text-sm cursor-pointer">
                  Entiendo que esta acción es permanente e irreversible
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox 
                  id="dataLoss" 
                  checked={confirmations.dataLoss}
                  onCheckedChange={(checked) => setConfirmations(prev => ({ ...prev, dataLoss: checked }))}
                />
                <label htmlFor="dataLoss" className="text-sm cursor-pointer">
                  Acepto perder todos mis datos, historial y preferencias
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox 
                  id="noRefund" 
                  checked={confirmations.noRefund}
                  onCheckedChange={(checked) => setConfirmations(prev => ({ ...prev, noRefund: checked }))}
                />
                <label htmlFor="noRefund" className="text-sm cursor-pointer">
                  Entiendo que mis puntos DIGGII no serán reembolsados
                </label>
              </div>
            </div>

            <Button 
              onClick={() => setStep(2)} 
              disabled={!allConfirmed}
              variant="destructive"
              className="w-full"
            >
              Continuar con la eliminación
            </Button>

            <Button 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="w-full"
            >
              Cancelar, quiero mantener mi cuenta
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">Confirma tu identidad</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Por seguridad, ingresa tu contraseña para confirmar la eliminación de tu cuenta.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <Input 
                    id="password"
                    type="password"
                    placeholder="Ingresa tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <Button 
              onClick={handleDeleteAccount} 
              disabled={!password || loading}
              variant="destructive"
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando cuenta...
                </>
              ) : (
                'Eliminar mi cuenta permanentemente'
              )}
            </Button>

            <Button 
              variant="outline" 
              onClick={() => setStep(1)}
              className="w-full"
              disabled={loading}
            >
              Volver
            </Button>
          </div>
        )}

        {/* Help Link */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          ¿Necesitas ayuda? <a href="mailto:soporte@mercapoli.com" className="text-primary hover:underline">Contactar soporte</a>
        </div>
      </main>
    </div>
  );
};

export default DeleteAccount;
